import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountPartiesApi, type AccountPartiesListParams } from '../api/accountParties';
import { ApiError } from '../api/client';
import type { AccountParty, AccountPartyCreateInput, AccountPartyUpdateInput, Page, UUID, WriteResult } from '../api/types';

export const acpKeys = {
  all: ['account-parties'] as const,
  list:   (p: AccountPartiesListParams) => ['account-parties', 'list', p] as const,
  detail: (id: UUID) => ['account-parties', 'detail', id] as const,
};
export function useAccountParties(params: AccountPartiesListParams) {
  return useQuery<Page<AccountParty>, ApiError>({ queryKey: acpKeys.list(params), queryFn: () => accountPartiesApi.list(params) });
}
export function useAccountParty(id: UUID | undefined) {
  return useQuery<AccountParty, ApiError>({ queryKey: acpKeys.detail(id ?? ''), queryFn: () => accountPartiesApi.get(id!), enabled: !!id });
}
export function useCreateAccountParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountParty>, ApiError, AccountPartyCreateInput>({
    mutationFn: accountPartiesApi.create,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: acpKeys.all }); if (!r.pending) qc.setQueryData(acpKeys.detail(r.entity.id), r.entity); },
  });
}
export function useUpdateAccountParty(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountParty>, ApiError, AccountPartyUpdateInput>({
    mutationFn: (p) => accountPartiesApi.update(id, p),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: acpKeys.all }); if (!r.pending) qc.setQueryData(acpKeys.detail(id), r.entity); },
  });
}
export function useDeleteAccountParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountParty>, ApiError, UUID>({
    mutationFn: accountPartiesApi.delete, onSuccess: () => qc.invalidateQueries({ queryKey: acpKeys.all }),
  });
}
export function useRestoreAccountParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountParty>, ApiError, UUID>({
    mutationFn: accountPartiesApi.restore, onSuccess: () => qc.invalidateQueries({ queryKey: acpKeys.all }),
  });
}
